import { images } from '@/asset';
import EmptyContent from '@/components/EmptyContent';
import { isEmbedded } from '@/config';
import { openCrisp } from '@/helpers';
import { IPagination } from '@/types';
import { Card, ColumnContentType, IndexTable } from '@shopify/polaris';
import { IndexTableHeading } from '@shopify/polaris/build/ts/src/components/IndexTable';
import { NonEmptyArray } from '@shopify/polaris/build/ts/src/types';
import { Range, SelectionType } from '@shopify/polaris/build/ts/src/utilities/index-provider';
import { memo, useEffect } from 'react';
import BasePagination from '../Pagination';
import SkeletonTable from '../SkeletonTable';
import { BaseTableStyled } from './styled';

interface IProps {
  isLoading: boolean;
  isFetching?: boolean;
  columnContentTypes: ColumnContentType[];
  headings: NonEmptyArray<IndexTableHeading>;
  actionItems?: any;
  rows: JSX.Element[];
  isHidePagination?: boolean;
  totalPage?: number;
  totalItem?: number;
  pagination?: IPagination;
  setPagination?: (pagination: IPagination) => void;
  data?: any[];
  selectedResources?: string[];
  allResourcesSelected: boolean;
  handleSelectionChange: (
    selectionType: SelectionType,
    toggleType: boolean,
    selection?: string | Range,
    position?: number,
  ) => void;
  selectable?: boolean;
  imgEmptySource: string;
  titleEmpty: string;
  titleActionBtnEmpty?: string;
  emptyAction?: () => void;
  isSearching: boolean;
  disabledBtnEmpty?: boolean;
  isNotUsingCard?: boolean;
}

const BaseTable = ({
  data,
  isLoading,
  isFetching,
  headings,
  columnContentTypes,
  rows,
  actionItems,
  isHidePagination,
  pagination,
  setPagination,
  totalPage,
  selectedResources,
  allResourcesSelected,
  handleSelectionChange,
  selectable,
  imgEmptySource,
  titleEmpty,
  titleActionBtnEmpty,
  emptyAction,
  isSearching,
  disabledBtnEmpty,
  isNotUsingCard,
}: IProps) => {
  const resourceName = {
    singular: 'method',
    plural: 'methods',
  };

  useEffect(() => {
    if (pagination && totalPage && pagination.page > totalPage) {
      setPagination?.({ page: totalPage, perPage: pagination?.perPage });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination?.page, totalPage]);

  const renderEmptyState = () => (
    <EmptyContent
      imgSource={isSearching ? images.emptySearch : imgEmptySource}
      title={isSearching ? "Sorry, we couldn't find a match for your search. Please try again." : titleEmpty}
      onAction={isSearching ? openCrisp : emptyAction}
      titleActionBtn={isSearching ? 'Contact support' : titleActionBtnEmpty}
      variant={isSearching ? undefined : 'primary'}
      disabled={disabledBtnEmpty}
    />
  );

  const renderPagination = () => {
    if (isHidePagination || !setPagination || !totalPage || !pagination) {
      return null;
    }

    return (
      <BasePagination
        isLoading={isFetching}
        totalPage={totalPage}
        handleChangePagination={setPagination}
        pagination={pagination}
      />
    );
  };

  const renderIndexTable = () => (
    <IndexTable
      resourceName={resourceName}
      itemCount={data?.length ?? 0}
      selectedItemsCount={allResourcesSelected ? 'All' : selectedResources?.length}
      onSelectionChange={handleSelectionChange}
      headings={headings}
      promotedBulkActions={actionItems}
      selectable={selectable}
      emptyState={renderEmptyState()}
    >
      {rows}
    </IndexTable>
  );

  return (
    <BaseTableStyled isInAdminPage={!isEmbedded}>
      {isLoading ? (
        <SkeletonTable column={columnContentTypes} headings={['Loading']} />
      ) : !isNotUsingCard ? (
        <Card padding="0">
          {renderIndexTable()}
          {renderPagination()}
        </Card>
      ) : (
        <>
          {renderIndexTable()}
          {renderPagination()}
        </>
      )}
    </BaseTableStyled>
  );
};

export default memo(BaseTable);
